<template>
  <v-navigation-drawer
    v-model="isOpen"
    app
    right
    temporary
    width="450"
    class="cart-drawer"
    color="#2C2C2C"
  >
    <v-card flat class="fill-height d-flex flex-column" color="transparent">
      <!-- Header -->
      <v-card-title class="cart-header white--text pa-6">
        <h2 class="text-h5 font-weight-bold">Resumen de compra</h2>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Empty State -->
      <div v-if="cartItems.length === 0" class="flex-grow-1 d-flex flex-column align-center justify-center pa-8">
        <v-icon size="100" color="grey">mdi-cart-outline</v-icon>
        <p class="text-h6 white--text mt-6 mb-2">Tu carrito está vacío</p>
        <p class="white--text text-center mb-6" style="opacity: 0.7;">
          Explora nuestra tienda y agrega productos NFCero
        </p>
        <v-btn color="primary" @click="goToShop" large>
          Ir a la tienda
        </v-btn>
      </div>

      <!-- Cart Items -->
      <v-card-text v-else class="flex-grow-1 overflow-y-auto pa-0">
        <v-list three-line class="py-0" color="transparent">
          <template v-for="(item, index) in cartItems">
            <v-list-item :key="`item-${index}`" class="cart-item px-6 py-4">
              <v-list-item-content>
                <!-- Nombre del producto/suscripción -->
                <v-list-item-title class="font-weight-bold white--text mb-2" style="font-size: 1.1rem;">
                  {{ item.productName || item.subscriptionName }}
                  <span v-if="item.type === 'subscription'" class="text-primary ml-2">
                    (Suscripción)
                  </span>
                </v-list-item-title>
                
                <!-- Detalles del producto -->
                <v-list-item-subtitle v-if="item.type === 'product'" class="mb-2">
                  <div v-if="item.productDetails" class="white--text" style="opacity: 0.8;">
                    <!-- Detalles de tarjeta custom -->
                    <span v-if="item.productDetails.type === 'custom'" class="custom-badge">
                      <v-icon x-small color="primary" class="mr-1">mdi-palette</v-icon>
                      Diseno personalizado
                    </span>
                    <!-- Detalles de anillo -->
                    <span v-if="item.productDetails.size">
                      Talla: {{ item.productDetails.size }}
                    </span>
                    <span v-if="item.productDetails.gender" class="ml-2">
                      | {{ item.productDetails.gender === 'mujer' ? 'Mujer' : 'Hombre' }}
                    </span>
                  </div>
                  
                  <!-- Controles de cantidad -->
                  <div class="d-flex align-center mt-2">
                    <v-btn icon small color="white" @click="decrementQuantity(item)">
                      <v-icon small>mdi-minus</v-icon>
                    </v-btn>
                    <span class="mx-3 white--text font-weight-bold">{{ item.quantity }}</span>
                    <v-btn icon small color="white" @click="incrementQuantity(item)">
                      <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </v-list-item-subtitle>

                <!-- Período de suscripción -->
                <v-list-item-subtitle v-else class="mt-2">
                  <v-chip-group
                    v-model="item.period"
                    mandatory
                    active-class="primary"
                    @change="updatePeriod(item)"
                  >
                    <v-chip small value="monthly" dark>Mensual</v-chip>
                    <v-chip small value="annual" dark>
                      Anual 
                      <v-icon small right color="success">mdi-tag</v-icon>
                      -20%
                    </v-chip>
                  </v-chip-group>
                </v-list-item-subtitle>

                <!-- Precio -->
                <v-list-item-subtitle class="mt-3">
                  <div class="d-flex align-items-center">
                    <span class="text-h6 text-primary font-weight-bold">
                      ${{ calculateItemTotal(item).toFixed(2) }}
                    </span>
                    <span v-if="item.discount > 0" class="ml-3 text-decoration-line-through white--text" style="opacity: 0.5;">
                      ${{ (item.price * item.quantity).toFixed(2) }}
                    </span>
                  </div>
                </v-list-item-subtitle>
              </v-list-item-content>

              <!-- Thumbnail para tarjeta personalizada (a la derecha) -->
              <v-list-item-action 
                v-if="item.productDetails && item.productDetails.type === 'custom' && item.productDetails.thumbnail"
                class="custom-card-action"
              >
                <div class="custom-card-thumbnail">
                  <v-img 
                    :src="item.productDetails.thumbnail" 
                    width="70"
                    height="44"
                    contain
                    class="rounded"
                  ></v-img>
                </div>
                <v-btn icon x-small @click="removeItem(item)" class="delete-btn-overlay">
                  <v-icon x-small color="error">mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>

              <!-- Botón eliminar (para items sin thumbnail) -->
              <v-list-item-action v-else>
                <v-btn icon small @click="removeItem(item)">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            
            <v-divider 
              :key="`divider-${index}`" 
              v-if="index < cartItems.length - 1"
              class="mx-6"
              dark
            ></v-divider>
          </template>
        </v-list>

        <!-- Información de envío 
        <div v-if="hasProduct" class="shipping-info pa-6 mt-4">
          <div class="white--text mb-2">
            <v-icon small color="white" class="mr-2">mdi-map-marker</v-icon>
            <span class="font-weight-bold">Dirección de envío</span>
          </div>
          <p class="white--text mb-0" style="opacity: 0.8; font-size: 0.9rem;">
            {{ shippingAddress || 'La dirección se configurará en el checkout' }}
          </p>
        </div> -->
      </v-card-text>

      <!-- Footer con Total y Checkout -->
      <v-card-actions v-if="cartItems.length > 0" class="cart-footer flex-column pa-6">
        <v-divider dark class="mb-4"></v-divider>
        
        <!-- Resumen de precios -->
        <div class="full-width mb-4">
          <div class="d-flex justify-space-between mb-2">
            <span class="white--text">Subtotal:</span>
            <span class="white--text font-weight-bold">${{ cartSubtotal.toFixed(2) }}</span>
          </div>
          
          <div v-if="hasProduct" class="d-flex justify-space-between mb-2">
            <span class="white--text text-overline" style="opacity: 0.7;">Envío:</span>
            <span class="white--text text-overline" style="opacity: 0.7;">Se calcula en checkout</span>
          </div>
          
          <div v-if="cartDiscount > 0" class="d-flex justify-space-between mb-2">
            <span class="success--text">Descuento:</span>
            <span class="success--text font-weight-bold">-${{ cartDiscount.toFixed(2) }}</span>
          </div>
          
          <v-divider dark class="my-3"></v-divider>
          
          <div class="d-flex justify-space-between">
            <span class="text-h6 white--text font-weight-bold">Subtotal:</span>
            <span class="text-h6 text-primary font-weight-bold">${{ cartTotal.toFixed(2) }}</span>
          </div>
          <p v-if="hasProduct" class="white--text text-caption mt-2 mb-0" style="opacity: 0.7;">
            <v-icon x-small>mdi-information-outline</v-icon>
            Total final incluirá envío según tu dirección
          </p>
        </div>

        <!-- Warning si el carrito está vacío o solo tiene un tipo de producto -->
        <v-alert
          v-if="shouldShowWarning"
          type="warning"
          dense
          text
          class="mb-4"
        >
          <small>Para una experiencia completa, agrega tanto un producto como una suscripción</small>
        </v-alert>

        <!-- Botones -->
        <v-btn
          block
          x-large
          color="black"
          class="checkout-btn"
          @click="proceedToCheckout"
          :disabled="!hasRequiredItems"
        >
          Continuar a pago
        </v-btn>
        
        <v-btn
          block
          text
          class="mt-2"
          @click="close"
          color="white"
        >
          Seguir Comprando
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'CartDrawer',

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      shippingAddress: '' // Se actualizará cuando el usuario ingrese dirección en el checkout
    }
  },

  computed: {
    ...mapGetters('cart', ['cartItems', 'cartSubtotal', 'cartDiscount', 'hasProduct', 'hasSubscription']),
    
    isOpen: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },

    hasRequiredItems() {
      // Validar que tenga al menos un producto O una suscripción
      return this.hasProduct || this.hasSubscription
    },

    shouldShowWarning() {
      // Mostrar warning solo cuando hay EXACTAMENTE uno de los dos tipos (XOR)
      // Si tiene producto pero NO suscripción, O tiene suscripción pero NO producto
      return (this.hasProduct && !this.hasSubscription) || (!this.hasProduct && this.hasSubscription)
    },

    cartTotal() {
      // En el carrito solo mostrar subtotal - descuento
      // El envío se calcula y suma en el CheckoutView
      return this.cartSubtotal - this.cartDiscount
    }
  },

  methods: {
    ...mapActions('cart', ['removeFromCart', 'updateQuantity', 'updateSubscriptionPeriod']),

    close() {
      this.isOpen = false
    },

    goToShop() {
      this.$router.push('/tienda')
      this.close()
    },

    removeItem(item) {
      const type = item.type
      const id = type === 'product' ? item.productId : item.subscriptionId
      this.removeFromCart({ type, id })
    },

    incrementQuantity(item) {
      this.updateQuantity({
        type: item.type,
        id: item.productId,
        quantity: item.quantity + 1
      })
    },

    decrementQuantity(item) {
      if (item.quantity > 1) {
        this.updateQuantity({
          type: item.type,
          id: item.productId,
          quantity: item.quantity - 1
        })
      }
    },

    updatePeriod(item) {
      this.updateSubscriptionPeriod({
        subscriptionId: item.subscriptionId,
        period: item.period
      })
    },

    calculateItemTotal(item) {
      let subtotal = item.price * (item.quantity || 1)
      
      // Si es una suscripción anual, multiplicar por 12 antes de aplicar descuento
      if (item.type === 'subscription' && item.period === 'annual') {
        subtotal = subtotal * 12
      }
      
      const discount = subtotal * (item.discount || 0) / 100
      return subtotal - discount
    },

    proceedToCheckout() {
      if (!this.hasRequiredItems) {
        this.$emit('show-message', {
          type: 'warning',
          text: 'Agrega un producto o una suscripción para continuar'
        })
        return
      }

      // Si ya está en checkout, no navegar
      if (this.$route.path === '/checkout') {
        this.close()
        return
      }

      // Ya NO validamos autenticación - permitir guest checkout
      this.close()
      this.$router.push('/checkout').catch(err => {
        // Ignorar errores de navegación redundante silenciosamente
        if (err.name !== 'NavigationDuplicated') {
          console.error('Error en navegación:', err)
        }
      })
    }
  }
}
</script>

<style scoped>
.cart-drawer {
  z-index: 999;
}

.cart-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #2C2C2C;
  border-bottom: 1px solid rgba(147, 27, 77, 0.3);
}

.cart-item {
  background: rgba(0, 0, 0, 0.2);
  transition: background 0.2s ease;
}

.cart-item:hover {
  background: rgba(0, 0, 0, 0.4);
}

.shipping-info {
  background: rgba(147, 27, 77, 0.15);
  border-radius: 8px;
  border-left: 4px solid #931B4D;
}

.cart-footer {
  position: sticky;
  bottom: 0;
  background: #2C2C2C;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(147, 27, 77, 0.3);
}

.checkout-btn {
  background: #000 !important;
  color: white !important;
  text-transform: none;
  font-size: 1.1rem;
  border: 1px solid #931B4D !important;
  transition: all 0.3s ease;
}

.checkout-btn:hover:not(:disabled) {
  box-shadow: 0 0 20px rgba(147, 27, 77, 0.5);
}

.full-width {
  width: 100%;
}

.custom-card-action {
  position: relative;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 8px;
}

.custom-card-thumbnail {
  border: 2px solid rgba(147, 27, 77, 0.5);
  border-radius: 8px !important;
  overflow: hidden;
}

.delete-btn-overlay {
  position: absolute;
  top: -6px;
  right: -6px;
  background: rgba(0, 0, 0, 0.8) !important;
  width: 20px !important;
  height: 20px !important;
}

.custom-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(147, 27, 77, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
}

/* Estilos personalizados para chips */
::v-deep .v-chip-group .v-chip {
  border: 1px solid rgba(255, 255, 255, 0.3);
}

::v-deep .v-chip-group .v-chip.primary {
  border-color: #931B4D;
}
</style>
